import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  AppWindowMac,
  ArrowDown,
  ArrowUp,
  BadgeCheck,
  Boxes,
  CheckCircle2,
  Code2,
  Diff,
  FileInput,
  FileJson,
  FolderOpen,
  GitPullRequest,
  ImageIcon,
  Images,
  PackageCheck,
  RefreshCw,
  Rocket,
  Save,
  SearchCheck,
  Send,
  ShieldCheck,
  Trash2,
  Upload,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Textarea } from '@/components/ui/Textarea';
import {
  useAppStoreDraftDiff,
  useAppStoreApps,
  useAppStoreApprovalInfo,
  useAppStoreSnapshot,
  useAppStoreSpec,
  useAppStoreUserImageFiles,
  useAttachAppStoreScreenshot,
  useApproveAppStoreDraft,
  useCreateAppStoreDraft,
  useDiscoverAppStoreApps,
  useImportAsoIntoDraft,
  usePatchAppStoreDraft,
  usePollAppStoreSubmission,
  useSyncAppStore,
  useSubmitAppStoreRelease,
  useUploadAppStoreScreenshot,
  useValidateAppStoreDraft,
  useVerifyAppStorePublicStore,
} from '@/features/app-store-releases/hooks';
import { useI18n } from '@/i18n';
import type { HttpError } from '@/lib/api/client';
import type {
  AppStoreAppCatalogItemResponse,
  AppStoreDiffItemResponse,
  AppStoreDraftResponse,
  AppStoreReleaseApprovalResponse,
  AppStoreReleaseMode,
  AppStoreReleaseSubmissionResponse,
  AppStoreReleaseVerifyPublicStoreResponse,
  AppStoreSnapshotResponse,
  AppStoreValidationItemResponse,
  AppStoreUserImageFileResponse,
} from '@/lib/api/appStoreReleases';
import { cn } from '@/lib/cn';

const DEFAULT_SUPPORTED_FEATURES = [
  'ai_companion_persona',
  'astrology',
  'daily_fortune',
  'relationship_matching',
  'tarot',
];
const DEFAULT_VISUAL_PROOF_FEATURES = ['ai_companion_persona', 'astrology'];

type JsonObject = Record<string, unknown>;
type Translate = (key: string, params?: Record<string, string>) => string;
type ScreenshotMaterialView = { item: JsonObject; index: number };
type ScreenshotMaterialGroup = { displayType: string; items: ScreenshotMaterialView[] };
type MetadataFieldKey =
  | 'title'
  | 'subtitle'
  | 'keywords'
  | 'description'
  | 'promotional_text'
  | 'whats_new'
  | 'privacy_policy_url'
  | 'subscription_disclosure'
  | 'disclaimer';

const METADATA_FIELD_ALIASES: Record<MetadataFieldKey, readonly string[]> = {
  title: ['title', 'name'],
  subtitle: ['subtitle'],
  keywords: ['keywords'],
  description: ['description'],
  promotional_text: ['promotional_text', 'promotionalText'],
  whats_new: ['whats_new', 'whatsNew', 'releaseNotes'],
  privacy_policy_url: ['privacy_policy_url', 'privacyPolicyUrl', 'privacyPolicyURL'],
  subscription_disclosure: ['subscription_disclosure'],
  disclaimer: ['disclaimer'],
};

const METADATA_FIELD_KEYS = Object.keys(METADATA_FIELD_ALIASES) as MetadataFieldKey[];
const UNASSIGNED_SCREENSHOT_DISPLAY_TYPE = '__unassigned__';

interface LocalizationCandidate {
  fields: JsonObject;
  locale: string;
  versionId: string;
}

interface AppInfoLocalizationCandidate {
  fields: JsonObject;
  locale: string;
  appStoreState: string;
}

function isRecord(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asRecord(value: unknown): JsonObject {
  return isRecord(value) ? value : {};
}

function valueAt(record: JsonObject, key: string): unknown {
  return record[key];
}

function textValue(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
}

function hasOwn(record: JsonObject, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function firstTextValue(...values: unknown[]): string {
  for (const value of values) {
    if (Array.isArray(value)) {
      const joined = value.map((item) => textValue(item).trim()).filter(Boolean).join(',');
      if (joined) {
        return joined;
      }
      continue;
    }
    const text = textValue(value).trim();
    if (text) {
      return text;
    }
  }
  return '';
}

function valueForAliases(record: JsonObject, aliases: readonly string[]): unknown {
  for (const alias of aliases) {
    if (hasOwn(record, alias)) {
      return record[alias];
    }
  }
  return undefined;
}

function hasLocalizedMetadataField(fields: JsonObject): boolean {
  return METADATA_FIELD_KEYS.some((fieldKey) =>
    METADATA_FIELD_ALIASES[fieldKey].some((alias) => hasOwn(fields, alias)),
  );
}

function selectedVersionIdFromPayload(payload: JsonObject): string {
  const releaseSelection = asRecord(payload.release_selection);
  return (
    textValue(payload.app_store_version_id) ||
    textValue(asRecord(releaseSelection.version).id) ||
    textValue(asRecord(payload.version).id)
  );
}

function primaryLocaleFromPayload(payload: JsonObject): string {
  const appInfo = asRecord(payload.app_info);
  const rawAttributes = asRecord(asRecord(appInfo.raw).attributes);
  return (
    textValue(payload.primary_locale) ||
    textValue(appInfo.primary_locale) ||
    textValue(appInfo.primaryLocale) ||
    textValue(rawAttributes.primaryLocale) ||
    'zh-Hans'
  );
}

function localizationCandidatesFromValue(
  value: unknown,
  inheritedVersionId = '',
): LocalizationCandidate[] {
  if (Array.isArray(value)) {
    return value.flatMap((item) => localizationCandidatesFromValue(item, inheritedVersionId));
  }
  if (!isRecord(value)) {
    return [];
  }

  const entryVersionId =
    textValue(value.version_id) ||
    textValue(value.versionId) ||
    textValue(asRecord(value.version).id) ||
    inheritedVersionId;

  const nested = value.localizations;
  if (Array.isArray(nested)) {
    return localizationCandidatesFromValue(nested, entryVersionId);
  }
  if (isRecord(nested)) {
    const nestedCandidates = Object.entries(nested).flatMap(([locale, fields]) => {
      if (!isRecord(fields)) {
        return [];
      }
      return [{ fields, locale, versionId: entryVersionId }];
    });
    if (nestedCandidates.length > 0) {
      return nestedCandidates;
    }
  }

  const mappedCandidates = Object.entries(value).flatMap(([locale, fields]) => {
    if (!isRecord(fields) || !hasLocalizedMetadataField(fields)) {
      return [];
    }
    return [{ fields, locale, versionId: entryVersionId }];
  });
  if (mappedCandidates.length > 0) {
    return mappedCandidates;
  }

  const attributes = asRecord(value.attributes);
  const fields = Object.keys(attributes).length > 0 ? attributes : value;
  const locale = textValue(fields.locale) || textValue(value.locale) || 'default';
  if (!hasLocalizedMetadataField(fields)) {
    return [];
  }
  return [{ fields, locale, versionId: entryVersionId }];
}

function selectedLocalizationFields(payload: JsonObject): JsonObject {
  const selectedVersionId = selectedVersionIdFromPayload(payload);
  const primaryLocale = primaryLocaleFromPayload(payload);
  const candidates = [
    ...localizationCandidatesFromValue(payload.localizations),
    ...localizationCandidatesFromValue(asRecord(payload.metadata).localizations),
  ];
  if (candidates.length === 0) {
    return {};
  }

  const versionCandidates = selectedVersionId
    ? candidates.filter((candidate) => !candidate.versionId || candidate.versionId === selectedVersionId)
    : candidates;
  const scopedCandidates = versionCandidates.length > 0 ? versionCandidates : candidates;
  return (
    scopedCandidates.find((candidate) => candidate.locale === primaryLocale)?.fields ??
    scopedCandidates.find((candidate) => candidate.locale === 'zh-Hans')?.fields ??
    scopedCandidates[0]?.fields ??
    {}
  );
}

function appStoreStateFromValue(value: unknown): string {
  const record = asRecord(value);
  const attributes = asRecord(record.attributes);
  return textValue(attributes.appStoreState) || textValue(attributes.state) || textValue(record.appStoreState) || textValue(record.state);
}

function appInfoLocalizationCandidatesFromPayload(payload: JsonObject): AppInfoLocalizationCandidate[] {
  const appInfo = asRecord(payload.app_info);
  const appInfos = Array.isArray(appInfo.app_infos) ? appInfo.app_infos : [];

  return appInfos.flatMap((item) => {
    if (!isRecord(item)) {
      return [];
    }
    const appStoreState = appStoreStateFromValue(item);
    const localizations = Array.isArray(item.localizations) ? item.localizations : [];
    return localizations.flatMap((localization) => {
      if (!isRecord(localization)) {
        return [];
      }
      const attributes = asRecord(localization.attributes);
      const fields = Object.keys(attributes).length > 0 ? attributes : localization;
      if (!hasLocalizedMetadataField(fields)) {
        return [];
      }
      return [
        {
          appStoreState,
          fields,
          locale: textValue(fields.locale) || textValue(localization.locale) || 'default',
        },
      ];
    });
  });
}

function selectedAppInfoLocalizationFields(payload: JsonObject): JsonObject {
  const selectedVersion = asRecord(payload.version);
  const releaseSelectionVersion = asRecord(asRecord(payload.release_selection).version);
  const selectedState = appStoreStateFromValue(selectedVersion) || appStoreStateFromValue(releaseSelectionVersion);
  const primaryLocale = primaryLocaleFromPayload(payload);
  const candidates = appInfoLocalizationCandidatesFromPayload(payload);
  if (candidates.length === 0) {
    return {};
  }

  const scopedCandidates = selectedState
    ? candidates.filter((candidate) => candidate.appStoreState === selectedState)
    : candidates;
  if (scopedCandidates.length === 0) {
    return {};
  }
  return (
    scopedCandidates.find((candidate) => candidate.locale === primaryLocale)?.fields ??
    (scopedCandidates.length === 1 ? scopedCandidates[0].fields : {})
  );
}

function metadataFromPayload(payload: JsonObject): JsonObject {
  const metadata = asRecord(payload.metadata);
  const versionLocalizationFields = selectedLocalizationFields(payload);
  const appInfoLocalizationFields = selectedAppInfoLocalizationFields(payload);
  const appInfo = asRecord(payload.app_info);
  const appInfoAttributes = asRecord(asRecord(appInfo.raw).attributes);
  const normalized: JsonObject = {};

  for (const fieldKey of METADATA_FIELD_KEYS) {
    if (hasOwn(metadata, fieldKey)) {
      normalized[fieldKey] = metadata[fieldKey];
      continue;
    }
    const appInfoFallback = valueForAliases(appInfoLocalizationFields, METADATA_FIELD_ALIASES[fieldKey]);
    const versionLocalizationFallback = valueForAliases(versionLocalizationFields, METADATA_FIELD_ALIASES[fieldKey]);
    const localizationFallback =
      fieldKey === 'title' || fieldKey === 'subtitle' || fieldKey === 'privacy_policy_url'
        ? appInfoFallback
        : versionLocalizationFallback;
    const fallbackText =
      fieldKey === 'title'
        ? firstTextValue(localizationFallback, appInfo.name, appInfoAttributes.name)
        : firstTextValue(localizationFallback);
    if (fallbackText) {
      normalized[fieldKey] = fallbackText;
    }
  }

  return normalized;
}

function payloadWithNormalizedMetadata(payload: JsonObject): JsonObject {
  const metadata = metadataFromPayload(payload);
  const existingMetadata = asRecord(payload.metadata);
  if (Object.keys(metadata).length === 0 && Object.keys(existingMetadata).length === 0) {
    return payload;
  }
  return {
    ...payload,
    metadata: {
      ...metadata,
      ...existingMetadata,
    },
  };
}

function formatValue(value: unknown, t?: Translate): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  if (typeof value === 'boolean') {
    return value
      ? t?.('appStoreReleaseWorkbench.common.yes') ?? 'Yes'
      : t?.('appStoreReleaseWorkbench.common.no') ?? 'No';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '-';
    }
    if (value.every((item) => item === null || typeof item !== 'object')) {
      return value.map((item) => formatValue(item, t)).join(', ');
    }
    return t?.('appStoreReleaseWorkbench.common.items', { count: String(value.length) }) ?? `${value.length} items`;
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

function toPrettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function parseJsonObject(value: string): JsonObject {
  const parsed = JSON.parse(value) as unknown;
  if (!isRecord(parsed)) {
    throw new Error('Draft payload must be a JSON object');
  }
  return parsed;
}

function patchDraftPayloadField(editorValue: string, sectionKey: string, fieldKey: string, value: string): string {
  const payload = editorValue ? parseJsonObject(editorValue) : {};
  const section = asRecord(payload[sectionKey]);
  return toPrettyJson({
    ...payload,
    [sectionKey]: {
      ...section,
      [fieldKey]: value,
    },
  });
}

function screenshotItemsFromPayload(payload: JsonObject): JsonObject[] {
  const media = asRecord(payload.media);
  const screenshots = media.screenshots ?? media.screenshot;
  if (Array.isArray(screenshots)) {
    return screenshots.filter(isRecord);
  }
  if (isRecord(screenshots)) {
    return [screenshots];
  }
  return [];
}

function filterScreenshotsByVersion(screenshots: JsonObject[], appStoreVersionId: string): JsonObject[] {
  if (!appStoreVersionId) {
    return screenshots;
  }
  return screenshots.filter((screenshot) => textValue(screenshot.app_store_version_id) === appStoreVersionId);
}

function screenshotItemsFromMediaSummary(mediaSummary: unknown, appStoreVersionId = ''): JsonObject[] {
  const summary = asRecord(mediaSummary);
  const screenshots = summary.screenshots;
  if (Array.isArray(screenshots)) {
    return filterScreenshotsByVersion(screenshots.filter(isRecord), appStoreVersionId);
  }

  const localizations = summary.localizations;
  if (!Array.isArray(localizations)) {
    return [];
  }
  const nestedScreenshots = localizations.flatMap((localization) => {
    const localizationRecord = asRecord(localization);
    const inheritedVersionId =
      textValue(localizationRecord.app_store_version_id) || textValue(localizationRecord.version_id);
    const sets = localizationRecord.screenshot_sets;
    if (!Array.isArray(sets)) {
      return [];
    }
    return sets.flatMap((set) => {
      const setScreenshots = asRecord(set).screenshots;
      if (!Array.isArray(setScreenshots)) {
        return [];
      }
      return setScreenshots.filter(isRecord).map((screenshot) => {
        if (textValue(screenshot.app_store_version_id) || !inheritedVersionId) {
          return screenshot;
        }
        return { ...screenshot, app_store_version_id: inheritedVersionId };
      });
    });
  });
  return filterScreenshotsByVersion(nestedScreenshots, appStoreVersionId);
}

function payloadWithScreenshotItems(editorValue: string, screenshots: JsonObject[]): string {
  const payload = editorValue ? parseJsonObject(editorValue) : {};
  const media = asRecord(payload.media);
  return toPrettyJson({
    ...payload,
    media: {
      ...media,
      screenshots,
    },
  });
}

function normalizeScreenshotOrder(items: JsonObject[]): JsonObject[] {
  return items.map((item, index) => ({ ...item, order: index }));
}

function removeScreenshotItem(editorValue: string, index: number): string {
  const payload = editorValue ? parseJsonObject(editorValue) : {};
  const screenshots = screenshotItemsFromPayload(payload);
  if (index < 0 || index >= screenshots.length) {
    return editorValue;
  }
  return payloadWithScreenshotItems(
    editorValue,
    normalizeScreenshotOrder(screenshots.filter((_, itemIndex) => itemIndex !== index)),
  );
}

function screenshotMaterialDisplayType(item: JsonObject): string {
  return textValue(item.display_type) || textValue(item.slot) || UNASSIGNED_SCREENSHOT_DISPLAY_TYPE;
}

function screenshotMaterialGroups(
  items: JsonObject[],
  activeDisplayType: string,
  knownDisplayTypes: string[] = [],
): ScreenshotMaterialGroup[] {
  const groups = new Map<string, ScreenshotMaterialGroup>();

  const ensureGroup = (displayType: string) => {
    const key = displayType || UNASSIGNED_SCREENSHOT_DISPLAY_TYPE;
    if (!groups.has(key)) {
      groups.set(key, { displayType: key, items: [] });
    }
    return groups.get(key)!;
  };

  if (activeDisplayType) {
    ensureGroup(activeDisplayType);
  }
  knownDisplayTypes.forEach(ensureGroup);
  items.forEach((item, index) => {
    ensureGroup(screenshotMaterialDisplayType(item)).items.push({ item, index });
  });

  return Array.from(groups.values()).sort((left, right) => {
    const leftKnownIndex = knownDisplayTypes.indexOf(left.displayType);
    const rightKnownIndex = knownDisplayTypes.indexOf(right.displayType);
    if (leftKnownIndex >= 0 && rightKnownIndex >= 0) {
      return leftKnownIndex - rightKnownIndex;
    }
    if (leftKnownIndex >= 0) return -1;
    if (rightKnownIndex >= 0) return 1;
    return 0;
  });
}

function moveScreenshotItemWithinGroup(editorValue: string, index: number, direction: -1 | 1): string {
  const payload = editorValue ? parseJsonObject(editorValue) : {};
  const screenshots = screenshotItemsFromPayload(payload);
  const current = screenshots[index];
  if (!current) {
    return editorValue;
  }
  const displayType = screenshotMaterialDisplayType(current);
  const groupIndices = screenshots
    .map((item, itemIndex) => ({ displayType: screenshotMaterialDisplayType(item), itemIndex }))
    .filter((entry) => entry.displayType === displayType)
    .map((entry) => entry.itemIndex);
  const groupPosition = groupIndices.indexOf(index);
  const targetIndex = groupIndices[groupPosition + direction];
  if (groupPosition < 0 || targetIndex === undefined) {
    return editorValue;
  }

  const nextScreenshots = [...screenshots];
  const [item] = nextScreenshots.splice(index, 1);
  nextScreenshots.splice(targetIndex, 0, item);
  return payloadWithScreenshotItems(editorValue, normalizeScreenshotOrder(nextScreenshots));
}

function removeScreenshotItemsByDisplayType(editorValue: string, displayType: string): string {
  const payload = editorValue ? parseJsonObject(editorValue) : {};
  const screenshots = screenshotItemsFromPayload(payload);
  return payloadWithScreenshotItems(
    editorValue,
    normalizeScreenshotOrder(screenshots.filter((item) => screenshotMaterialDisplayType(item) !== displayType)),
  );
}

function parseOptionalNonNegativeInt(value: string, errorMessage: string): number | null {
  if (!value.trim()) {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) {
    throw new Error(errorMessage);
  }
  return parsed;
}

function screenshotDisplayTypeOptions(spec: Record<string, unknown> | undefined, currentDisplayType: string): string[] {
  const screenshotSpec = asRecord(asRecord(spec?.media).screenshot);
  const allowedDimensions = screenshotSpec.allowed_dimensions;
  const options = Array.isArray(allowedDimensions)
    ? allowedDimensions
        .map((entry) => textValue(asRecord(entry).display_type))
        .filter(Boolean)
    : [];
  if (currentDisplayType && !options.includes(currentDisplayType)) {
    return [currentDisplayType, ...options];
  }
  return options;
}

function screenshotDimensionsLabel(spec: Record<string, unknown> | undefined, displayType: string, t: Translate): string {
  const screenshotSpec = asRecord(asRecord(spec?.media).screenshot);
  const allowedDimensions = screenshotSpec.allowed_dimensions;
  if (!Array.isArray(allowedDimensions)) {
    return '-';
  }
  const match = allowedDimensions.find((entry) => textValue(asRecord(entry).display_type) === displayType);
  const sizes = asRecord(match).sizes;
  if (!Array.isArray(sizes)) {
    return '-';
  }
  const labels = sizes
    .map((size) => {
      const record = asRecord(size);
      const width = textValue(record.width);
      const height = textValue(record.height);
      return width && height ? `${width}x${height}` : '';
    })
    .filter(Boolean);
  return labels.length > 0 ? labels.join(', ') : t('appStoreReleaseWorkbench.editor.noDimensionSpec');
}

function screenshotDisplayTypeLabel(displayType: string, t: Translate): string {
  if (displayType === UNASSIGNED_SCREENSHOT_DISPLAY_TYPE) {
    return t('appStoreReleaseWorkbench.editor.unassignedDisplayType');
  }
  const labels: Record<string, string> = {
    iphone_5_8: t('appStoreReleaseWorkbench.editor.displayTypeIphone58'),
    iphone_5_5: t('appStoreReleaseWorkbench.editor.displayTypeIphone55'),
    iphone_6_1: t('appStoreReleaseWorkbench.editor.displayTypeIphone61'),
    iphone_6_5: t('appStoreReleaseWorkbench.editor.displayTypeIphone65'),
    iphone_6_7: t('appStoreReleaseWorkbench.editor.displayTypeIphone67'),
    iphone_6_9: t('appStoreReleaseWorkbench.editor.displayTypeIphone69'),
    ipad_pro_12_9: t('appStoreReleaseWorkbench.editor.displayTypeIpadPro129'),
    ipad_12_9: t('appStoreReleaseWorkbench.editor.displayTypeIpad129'),
  };
  return labels[displayType] ?? displayType;
}

function imageFileLabel(file: AppStoreUserImageFileResponse, t: Translate): string {
  return `${file.filename} · ${byteSizeLabel(file.size, t)}`;
}

function screenshotPreviewUrl(
  item: JsonObject,
  fileByObjectName: Map<string, AppStoreUserImageFileResponse>,
): string {
  const directUrl = textValue(item.download_url) || textValue(item.preview_url) || textValue(item.url);
  if (directUrl) {
    return directUrl;
  }
  const objectName = textValue(item.object_name);
  return objectName ? fileByObjectName.get(objectName)?.url ?? '' : '';
}

function screenshotItemDimensionsLabel(item: JsonObject): string {
  const width = textValue(item.width);
  const height = textValue(item.height);
  return width && height ? `${width}x${height}` : '';
}

function byteSizeLabel(value: unknown, t: Translate): string {
  const bytes = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '-';
  }
  if (bytes < 1024 * 1024) {
    return t('appStoreReleaseWorkbench.common.kilobytes', { count: String(Math.ceil(bytes / 1024)) });
  }
  return t('appStoreReleaseWorkbench.common.megabytes', { count: (bytes / 1024 / 1024).toFixed(1) });
}

function fieldLimitLabel(value: string, limit: number, t: Translate): string {
  return t('appStoreReleaseWorkbench.editor.fieldLimit', {
    count: String(value.length),
    limit: String(limit),
  });
}

function versionLabel(version: JsonObject): string {
  const attributes = asRecord(version.attributes);
  return (
    textValue(valueAt(version, 'version_string')) ||
    textValue(valueAt(version, 'versionString')) ||
    textValue(valueAt(attributes, 'versionString')) ||
    textValue(valueAt(version, 'version')) ||
    textValue(valueAt(version, 'id')) ||
    'new-version'
  );
}

function versionState(version: JsonObject | undefined): string | null {
  if (!version) {
    return null;
  }
  const attributes = asRecord(version.attributes);
  return textValue(valueAt(version, 'state')) || textValue(valueAt(attributes, 'appStoreState')) || null;
}

function appStoreVersionId(version: JsonObject | undefined): string {
  return version ? textValue(valueAt(version, 'id')) : '';
}

function buildKey(build: JsonObject): string {
  return (
    textValue(valueAt(build, 'id')) ||
    textValue(valueAt(build, 'version')) ||
    textValue(valueAt(asRecord(build.attributes), 'version')) ||
    'build'
  );
}

function buildLabel(build: JsonObject): string {
  const attributes = asRecord(build.attributes);
  const version = textValue(valueAt(build, 'version')) || textValue(valueAt(attributes, 'version'));
  const uploaded = textValue(valueAt(build, 'uploaded_date')) || textValue(valueAt(attributes, 'uploadedDate'));
  const id = textValue(valueAt(build, 'id'));
  return [version || id || 'build', uploaded].filter(Boolean).join(' / ');
}

function snapshotToDraftPayload(snapshot: AppStoreSnapshotResponse, versionString: string, selectedBuildKey: string) {
  const selectedVersion = snapshot.versions.find((version) => versionLabel(version) === versionString);
  const selectedVersionId = appStoreVersionId(selectedVersion);
  const selectedBuild = snapshot.builds.find((build) => buildKey(build) === selectedBuildKey) ?? snapshot.builds[0];
  return payloadWithNormalizedMetadata({
    app_store_version_id: selectedVersionId || null,
    asc_app_id: snapshot.asc_app_id,
    app_info: snapshot.app_info,
    bundle_id: snapshot.bundle_id,
    versions: snapshot.versions,
    version: selectedVersion ?? null,
    localizations: snapshot.localizations,
    builds: snapshot.builds,
    review_detail: snapshot.review_detail,
    media_summary: snapshot.media_summary,
    iap_subscription_summary: snapshot.iap_subscription_summary,
    price_availability_summary: snapshot.price_availability_summary,
    release_selection: {
      version_string: versionString,
      version: selectedVersion ?? null,
      build: selectedBuild ?? null,
    },
  });
}

function initialVersion(snapshot: AppStoreSnapshotResponse | undefined): string {
  if (!snapshot || snapshot.versions.length === 0) {
    return '';
  }
  return versionLabel(snapshot.versions[0]);
}

function initialBuild(snapshot: AppStoreSnapshotResponse | undefined): string {
  if (!snapshot || snapshot.builds.length === 0) {
    return '';
  }
  return buildKey(snapshot.builds[0]);
}

function mutationError(...errors: Array<Error | null | undefined>): string | null {
  return errors.find(Boolean)?.message ?? null;
}

function isHttpNotFound(error: Error | null | undefined): boolean {
  return (error as Partial<HttpError> | null | undefined)?.statusCode === 404;
}

function syncErrorMessage(error: Error | null | undefined, appKey: string, t: Translate): string | null {
  if (!error) {
    return null;
  }
  if (isHttpNotFound(error)) {
    return t('appStoreReleaseWorkbench.errors.syncNotFound', { appKey });
  }
  return error.message;
}

function severityLabel(severity: AppStoreValidationItemResponse['severity'], t: Translate): string {
  return t(`appStoreReleaseWorkbench.severity.${severity}`);
}

function changeTypeLabel(changeType: string, t: Translate): string {
  const labels: Record<string, string> = {
    added: t('appStoreReleaseWorkbench.changeType.added'),
    changed: t('appStoreReleaseWorkbench.changeType.changed'),
    removed: t('appStoreReleaseWorkbench.changeType.removed'),
  };
  return labels[changeType] ?? changeType;
}

function releaseModeLabel(mode: AppStoreReleaseMode, t: Translate): string {
  return mode === 'automatic'
    ? t('appStoreReleaseWorkbench.release.modeAutomatic')
    : t('appStoreReleaseWorkbench.release.modeManual');
}

function releaseStateLabel(state: string | null | undefined, t: Translate): string {
  if (!state) {
    return '-';
  }
  const labels: Record<string, string> = {
    approved: t('appStoreReleaseWorkbench.release.stateApproved'),
    approved_pending_release: t('appStoreReleaseWorkbench.release.stateApprovedPendingRelease'),
    binding_build: t('appStoreReleaseWorkbench.release.stateBindingBuild'),
    creating_review_submission: t('appStoreReleaseWorkbench.release.stateCreatingReviewSubmission'),
    failed: t('appStoreReleaseWorkbench.release.stateFailed'),
    in_review: t('appStoreReleaseWorkbench.release.stateInReview'),
    rejected: t('appStoreReleaseWorkbench.release.stateRejected'),
    released: t('appStoreReleaseWorkbench.release.stateReleased'),
    submitted: t('appStoreReleaseWorkbench.release.stateSubmitted'),
    submitted_for_review: t('appStoreReleaseWorkbench.release.stateSubmitted'),
    waiting_for_review: t('appStoreReleaseWorkbench.release.stateWaitingForReview'),
    writing_metadata: t('appStoreReleaseWorkbench.release.stateWritingMetadata'),
  };
  return labels[state] ?? state;
}

function eventMessage(event: Record<string, unknown>, t: Translate): string {
  return textValue(valueAt(event, 'message')) || t('appStoreReleaseWorkbench.release.eventNoMessage');
}

function Counter({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: number | string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-medium uppercase text-muted-foreground">{label}</span>
      </div>
      <div className="mt-2 text-xl font-semibold text-foreground">{value}</div>
    </div>
  );
}

function StatusChip({
  tone,
  children,
}: {
  tone: 'gray' | 'green' | 'red' | 'amber' | 'blue';
  children: React.ReactNode;
}) {
  const classes = {
    amber: 'border-warning/30 bg-warning/10 text-warning',
    blue: 'border-info/30 bg-info/10 text-info',
    gray: 'border-border bg-muted/40 text-foreground',
    green: 'border-success/30 bg-success/10 text-success',
    red: 'border-destructive/30 bg-destructive/10 text-destructive',
  };
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium', classes[tone])}>
      {children}
    </span>
  );
}

function ErrorBanner({ message }: { message: string | null }) {
  if (!message) {
    return null;
  }
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-3 text-sm text-destructive">
      {message}
    </div>
  );
}

function SnapshotItems({ snapshot, t }: { snapshot: AppStoreSnapshotResponse | undefined; t: Translate }) {
  const versions = snapshot?.versions ?? [];
  const localizations = snapshot?.localizations ?? [];
  const builds = snapshot?.builds ?? [];

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
          {t('appStoreReleaseWorkbench.snapshot.versions')}
        </div>
        <div className="space-y-2">
          {versions.slice(0, 4).map((version, index) => (
            <div key={`${versionLabel(version)}-${index}`} className="rounded-lg border border-border px-3 py-2 text-sm">
              <div className="font-medium text-foreground">{versionLabel(version)}</div>
              <div className="mt-1 text-xs text-muted-foreground">{formatValue(versionState(version), t)}</div>
            </div>
          ))}
          {versions.length === 0 && (
            <div className="text-sm text-muted-foreground">{t('appStoreReleaseWorkbench.snapshot.noVersions')}</div>
          )}
        </div>
      </div>

      <div>
        <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
          {t('appStoreReleaseWorkbench.snapshot.builds')}
        </div>
        <div className="space-y-2">
          {builds.slice(0, 4).map((build, index) => (
            <div key={`${buildKey(build)}-${index}`} className="rounded-lg border border-border px-3 py-2 text-sm">
              <div className="font-medium text-foreground">{buildLabel(build)}</div>
              <div className="mt-1 break-all text-xs text-muted-foreground">{formatValue(valueAt(build, 'id'), t)}</div>
            </div>
          ))}
          {builds.length === 0 && (
            <div className="text-sm text-muted-foreground">{t('appStoreReleaseWorkbench.snapshot.noBuilds')}</div>
          )}
        </div>
      </div>

      <div>
        <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
          {t('appStoreReleaseWorkbench.snapshot.localizations')}
        </div>
        <div className="flex flex-wrap gap-2">
          {localizations.slice(0, 10).map((localization, index) => {
            const locale =
              textValue(valueAt(localization, 'locale')) ||
              textValue(valueAt(asRecord(localization.attributes), 'locale')) ||
              `loc-${index + 1}`;
            return <StatusChip key={`${locale}-${index}`} tone="gray">{locale}</StatusChip>;
          })}
          {localizations.length === 0 && (
            <div className="text-sm text-muted-foreground">{t('appStoreReleaseWorkbench.snapshot.noLocalizations')}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function DiffItem({ item, t }: { item: AppStoreDiffItemResponse; t: Translate }) {
  const riskTone = item.risk === 'blocking' ? 'red' : item.risk === 'warning' ? 'amber' : 'gray';
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-mono text-xs text-muted-foreground">{item.path}</div>
          <div className="mt-1 text-sm font-medium text-foreground">{item.message}</div>
        </div>
        <StatusChip tone={riskTone}>{changeTypeLabel(item.change_type, t)}</StatusChip>
      </div>
      <div className="mt-3 grid gap-2 text-xs md:grid-cols-2">
        <div className="min-w-0 rounded bg-muted/40 p-2">
          <div className="mb-1 font-medium text-muted-foreground">{t('appStoreReleaseWorkbench.diff.before')}</div>
          <div className="break-words font-mono text-foreground">{formatValue(item.before, t)}</div>
        </div>
        <div className="min-w-0 rounded bg-info/10 p-2">
          <div className="mb-1 font-medium text-info">{t('appStoreReleaseWorkbench.diff.after')}</div>
          <div className="break-words font-mono text-info">{formatValue(item.after, t)}</div>
        </div>
      </div>
    </div>
  );
}

function ValidationItem({ item, t }: { item: AppStoreValidationItemResponse; t: Translate }) {
  const tone = item.severity === 'blocking' ? 'red' : item.severity === 'warning' ? 'amber' : 'blue';
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-muted-foreground">{item.field_path}</div>
          <div className="mt-1 text-sm font-medium text-foreground">{item.message}</div>
        </div>
        <StatusChip tone={tone}>{severityLabel(item.severity, t)}</StatusChip>
      </div>
      <div className="mt-2 text-xs text-muted-foreground">{item.rule_id}</div>
    </div>
  );
}

function validationCounts(items: AppStoreValidationItemResponse[]) {
  return items.reduce(
    (counts, item) => {
      counts[item.severity] += 1;
      return counts;
    },
    { blocking: 0, info: 0, warning: 0 },
  );
}

function specRequirementRows(spec: Record<string, unknown> | undefined, t: Translate) {
  const fields = asRecord(spec?.fields);
  const media = asRecord(spec?.media);
  return [
    ['metadata.keywords', t('appStoreReleaseWorkbench.spec.keywordsRequirement'), fields.metadata ? 'schema' : 'default'],
    ['media.screenshots', t('appStoreReleaseWorkbench.spec.screenshotsRequirement'), media.screenshot ? 'schema' : 'default'],
    ['subscription disclosure', t('appStoreReleaseWorkbench.spec.subscriptionRequirement'), fields.metadata ? 'schema' : 'default'],
    ['privacy policy URL', t('appStoreReleaseWorkbench.spec.privacyRequirement'), fields.metadata ? 'schema' : 'default'],
  ];
}

function appCatalogLabel(app: AppStoreAppCatalogItemResponse): string {
  const name = app.display_name || app.app_key;
  if (app.sku && app.sku !== app.app_key) {
    return `${name} (${app.sku})`;
  }
  return name;
}

export function AppStoreReleaseWorkbenchPage() {
  const { t } = useI18n();
  const [appKey, setAppKey] = useState('');
  const [versionString, setVersionString] = useState('');
  const [selectedBuildKey, setSelectedBuildKey] = useState('');
  const [draft, setDraft] = useState<AppStoreDraftResponse | null>(null);
  const [editorValue, setEditorValue] = useState('');
  const [editorError, setEditorError] = useState<string | null>(null);
  const [reason, setReason] = useState(() => t('appStoreReleaseWorkbench.editor.defaultReason'));
  const [asoSourcePath, setAsoSourcePath] = useState('');
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotLocale, setScreenshotLocale] = useState('zh-Hans');
  const [screenshotDisplayType, setScreenshotDisplayType] = useState('iphone_6_9');
  const [screenshotSlot, setScreenshotSlot] = useState('iphone_6_9');
  const [screenshotOrder, setScreenshotOrder] = useState('');
  const [screenshotReplaceIndex, setScreenshotReplaceIndex] = useState('');
  const [selectedExistingObjectName, setSelectedExistingObjectName] = useState('');
  const [editorTab, setEditorTab] = useState('aso');
  const [releaseMode, setReleaseMode] = useState<AppStoreReleaseMode>('manual');
  const [approvedHash, setApprovedHash] = useState('');
  const [approval, setApproval] = useState<AppStoreReleaseApprovalResponse | null>(null);
  const [submission, setSubmission] = useState<AppStoreReleaseSubmissionResponse | null>(null);
  const [publicStoreVerification, setPublicStoreVerification] =
    useState<AppStoreReleaseVerifyPublicStoreResponse | null>(null);

  const appCatalogQuery = useAppStoreApps();
  const discoverAppsMutation = useDiscoverAppStoreApps();
  const appCatalog = appCatalogQuery.data?.items ?? [];
  const selectedApp = appCatalog.find((app) => app.app_key === appKey) ?? null;
  const snapshotQuery = useAppStoreSnapshot(appKey);
  const syncMutation = useSyncAppStore(appKey);
  const latestSnapshot = snapshotQuery.data;
  const specQuery = useAppStoreSpec(latestSnapshot?.spec_version ?? null);
  const latestSpec = specQuery.data;

  const createDraftMutation = useCreateAppStoreDraft(appKey);
  const patchDraftMutation = usePatchAppStoreDraft(draft?.id ?? null);
  const importAsoMutation = useImportAsoIntoDraft(draft?.id ?? null);
  const uploadScreenshotMutation = useUploadAppStoreScreenshot(draft?.id ?? null);
  const attachScreenshotMutation = useAttachAppStoreScreenshot(draft?.id ?? null);
  const userImageFilesQuery = useAppStoreUserImageFiles(Boolean(draft));
  const diffQuery = useAppStoreDraftDiff(draft?.id ?? null);
  const validateMutation = useValidateAppStoreDraft(draft?.id ?? null);
  const approveDraftMutation = useApproveAppStoreDraft(draft?.id ?? null);
  const submitReleaseMutation = useSubmitAppStoreRelease(approval?.snapshot_id ?? null);
  const pollSubmissionMutation = usePollAppStoreSubmission(submission?.id ?? null);
  const verifyPublicStoreMutation = useVerifyAppStorePublicStore(submission?.id ?? null);

  const versionOptions = latestSnapshot?.versions ?? [];
  const buildOptions = latestSnapshot?.builds ?? [];
  const selectedVersion = versionOptions.find((version) => versionLabel(version) === versionString);
  let editorPayload: JsonObject = {};
  try {
    editorPayload = editorValue ? parseJsonObject(editorValue) : {};
  } catch {
    editorPayload = {};
  }
  const metadataPayload = metadataFromPayload(editorPayload);
  const screenshotMaterials = screenshotItemsFromPayload(editorPayload);
  const ascScreenshotMaterials = useMemo(
    () => screenshotItemsFromMediaSummary(latestSnapshot?.media_summary, textValue(selectedVersion?.id)),
    [latestSnapshot?.media_summary, selectedVersion?.id],
  );
  const userImageFiles = useMemo(
    () => userImageFilesQuery.data?.files ?? [],
    [userImageFilesQuery.data?.files],
  );
  const fileByObjectName = useMemo(
    () => new Map(userImageFiles.map((file) => [file.object_name, file])),
    [userImageFiles],
  );
  const selectedExistingFile = selectedExistingObjectName
    ? fileByObjectName.get(selectedExistingObjectName) ?? null
    : null;
  const screenshotDisplayTypes = screenshotDisplayTypeOptions(latestSpec?.spec, screenshotDisplayType);
  const groupedAscScreenshotMaterials = screenshotMaterialGroups(ascScreenshotMaterials, '');
  const groupedScreenshotMaterials = screenshotMaterialGroups(
    screenshotMaterials,
    screenshotDisplayType,
    screenshotDisplayTypes,
  );
  const isDirty = Boolean(draft && editorValue && editorValue !== toPrettyJson(draft.draft_payload));
  const isDraftEditable = Boolean(draft);
  const canSaveDraft = Boolean(draft && isDirty && !patchDraftMutation.isPending);
  const canValidateDraft = Boolean(draft && !isDirty && !validateMutation.isPending);
  const canUploadScreenshot = Boolean(draft && !isDirty && screenshotFile && !uploadScreenshotMutation.isPending);
  const canAttachScreenshot = Boolean(
    draft && !isDirty && selectedExistingObjectName && !attachScreenshotMutation.isPending,
  );
  const snapshotNotFound = isHttpNotFound(snapshotQuery.error);
  const validation =
    draft && validateMutation.data?.draft_revision_id === draft.current_revision_id
      ? validateMutation.data
      : undefined;
  const counts = validationCounts(validation?.items ?? []);
  const releaseControlsAvailable = Boolean(draft && validation && !validation.has_blocking_errors && !isDirty);
  const approvalInfoRequest =
    releaseControlsAvailable && draft && selectedBuildKey
      ? {
          build_id: selectedBuildKey,
          draft_revision_id: draft.current_revision_id,
          release_mode: releaseMode,
          review_notes: reason || null,
          validation_run_id: null,
        }
      : null;
  const approvalInfoQuery = useAppStoreApprovalInfo(draft?.id ?? null, approvalInfoRequest);
  const expectedApprovalHash = approvalInfoQuery.data?.approved_hash ?? '';
  const approvedHashValue = approvedHash.trim();
  const approvedHashMatchesDraft = Boolean(expectedApprovalHash && approvedHashValue === expectedApprovalHash);
  const approvalMatchesSelection = Boolean(
    approval &&
      approval.build_id === selectedBuildKey &&
      approval.approved_hash === approvedHashValue &&
      approval.release_mode === releaseMode,
  );
  const canApproveRelease = Boolean(
    releaseControlsAvailable &&
      selectedBuildKey &&
      approvedHashMatchesDraft &&
      !approvalInfoQuery.isFetching &&
      !approveDraftMutation.isPending,
  );
  const canSubmitRelease = Boolean(
    releaseControlsAvailable && approvalMatchesSelection && !submitReleaseMutation.isPending,
  );
  const workflowNotice = !draft
    ? {
        title: t('appStoreReleaseWorkbench.editor.workflowPreviewTitle'),
        body: t('appStoreReleaseWorkbench.editor.workflowPreviewBody'),
        tone: 'blue' as const,
      }
    : isDirty
      ? {
          title: t('appStoreReleaseWorkbench.editor.workflowDirtyTitle'),
          body: t('appStoreReleaseWorkbench.editor.workflowDirtyBody'),
          tone: 'amber' as const,
        }
      : {
          title: t('appStoreReleaseWorkbench.editor.workflowSavedTitle'),
          body: t('appStoreReleaseWorkbench.editor.workflowSavedBody'),
          tone: 'green' as const,
        };

  const resetReleaseFlow = useCallback(() => {
    setApprovedHash('');
    setApproval(null);
    setSubmission(null);
    setPublicStoreVerification(null);
    approveDraftMutation.reset();
    submitReleaseMutation.reset();
    pollSubmissionMutation.reset();
    verifyPublicStoreMutation.reset();
  }, [approveDraftMutation, pollSubmissionMutation, submitReleaseMutation, verifyPublicStoreMutation]);

  useEffect(() => {
    if (!latestSnapshot || !draft) {
      return;
    }
    if (draft.base_snapshot_revision === latestSnapshot.snapshot_revision && draft.spec_hash === latestSnapshot.spec_hash) {
      return;
    }
    const nextVersion = initialVersion(latestSnapshot);
    const nextBuild = initialBuild(latestSnapshot);
    setDraft(null);
    setVersionString(nextVersion);
    setSelectedBuildKey(nextBuild);
    setEditorValue(toPrettyJson(snapshotToDraftPayload(latestSnapshot, nextVersion, nextBuild)));
    setEditorError(null);
    createDraftMutation.reset();
    patchDraftMutation.reset();
    validateMutation.reset();
    resetReleaseFlow();
  }, [createDraftMutation, draft, latestSnapshot, patchDraftMutation, resetReleaseFlow, validateMutation]);

  useEffect(() => {
    if (!latestSnapshot || draft) {
      return;
    }
    const nextVersion = initialVersion(latestSnapshot);
    const nextBuild = initialBuild(latestSnapshot);
    setVersionString((current) => current || nextVersion);
    setSelectedBuildKey((current) => current || nextBuild);
    setEditorValue(toPrettyJson(snapshotToDraftPayload(latestSnapshot, nextVersion, nextBuild)));
    setEditorError(null);
  }, [draft, latestSnapshot]);

  useEffect(() => {
    if (approvalInfoQuery.data?.approved_hash) {
      setApprovedHash(approvalInfoQuery.data.approved_hash);
    }
  }, [approvalInfoQuery.data?.approved_hash]);

  const reviewError =
    syncErrorMessage(syncMutation.error, appKey, t) ??
    mutationError(
      appCatalogQuery.error,
      discoverAppsMutation.error,
      createDraftMutation.error,
      patchDraftMutation.error,
      importAsoMutation.error,
      uploadScreenshotMutation.error,
      attachScreenshotMutation.error,
      userImageFilesQuery.error,
      validateMutation.error,
      approveDraftMutation.error,
      submitReleaseMutation.error,
      pollSubmissionMutation.error,
      verifyPublicStoreMutation.error,
      approvalInfoQuery.error,
      diffQuery.error,
    );

  const selectApp = (nextAppKey: string) => {
    setAppKey(nextAppKey);
    setDraft(null);
    setEditorValue('');
    setEditorError(null);
    setVersionString('');
    setSelectedBuildKey('');
    setScreenshotFile(null);
    setScreenshotSlot('iphone_6_9');
    setScreenshotOrder('');
    setScreenshotReplaceIndex('');
    setSelectedExistingObjectName('');
    syncMutation.reset();
    uploadScreenshotMutation.reset();
    attachScreenshotMutation.reset();
    validateMutation.reset();
    resetReleaseFlow();
  };

  const createDraft = () => {
    if (!latestSnapshot || !versionString.trim()) {
      return;
    }
    let payload: JsonObject;
    try {
      payload = editorValue
        ? parseJsonObject(editorValue)
        : snapshotToDraftPayload(latestSnapshot, versionString, selectedBuildKey);
    } catch {
      setEditorError(t('appStoreReleaseWorkbench.errors.invalidDraftJson'));
      return;
    }
    createDraftMutation.mutate(
      {
        base_snapshot_revision: latestSnapshot.snapshot_revision,
        draft_payload: payload,
        reason,
        source: 'manual',
        spec_hash: latestSnapshot.spec_hash,
        version_string: versionString.trim(),
      },
      {
        onError: (error) => setEditorError(error.message),
        onSuccess: (nextDraft) => {
          setDraft(nextDraft);
          setEditorValue(toPrettyJson(nextDraft.draft_payload));
          setEditorError(null);
          validateMutation.reset();
          resetReleaseFlow();
        },
      },
    );
  };

  const saveDraft = () => {
    if (!draft) {
      return;
    }
    let payload: JsonObject;
    try {
      payload = parseJsonObject(editorValue);
    } catch {
      setEditorError(t('appStoreReleaseWorkbench.errors.invalidDraftJson'));
      return;
    }
    patchDraftMutation.mutate(
      {
        base_snapshot_revision: draft.base_snapshot_revision,
        draft_revision_id: draft.current_revision_id,
        patch_payload: payload,
        reason,
        source: 'manual',
        spec_hash: draft.spec_hash,
      },
      {
        onSuccess: (nextDraft) => {
          setDraft(nextDraft);
          setEditorValue(toPrettyJson(nextDraft.draft_payload));
          setEditorError(null);
          validateMutation.reset();
          resetReleaseFlow();
        },
      },
    );
  };

  const importAso = () => {
    if (!draft || !asoSourcePath.trim()) {
      return;
    }
    importAsoMutation.mutate(
      {
        base_snapshot_revision: draft.base_snapshot_revision,
        draft_revision_id: draft.current_revision_id,
        reason,
        source_path: asoSourcePath.trim(),
        spec_hash: draft.spec_hash,
      },
      {
        onSuccess: (nextDraft) => {
          setDraft(nextDraft);
          setEditorValue(toPrettyJson(nextDraft.draft_payload));
          setEditorError(null);
          validateMutation.reset();
          resetReleaseFlow();
        },
      },
    );
  };

  const updateMetadataField = (fieldKey: string, value: string) => {
    if (!draft) {
      return;
    }
    try {
      setEditorValue(patchDraftPayloadField(editorValue, 'metadata', fieldKey, value));
      setEditorError(null);
    } catch {
      setEditorError(t('appStoreReleaseWorkbench.errors.invalidDraftJson'));
    }
  };

  const prepareScreenshotAppend = (displayType: string) => {
    setScreenshotDisplayType(displayType);
    setScreenshotSlot(displayType === UNASSIGNED_SCREENSHOT_DISPLAY_TYPE ? '' : displayType);
    setScreenshotOrder('');
    setScreenshotReplaceIndex('');
    setScreenshotFile(null);
  };

  const clearPendingScreenshotReplacement = () => {
    setScreenshotOrder('');
    setScreenshotReplaceIndex('');
    setScreenshotFile(null);
    setSelectedExistingObjectName('');
  };

  const prepareScreenshotReplacement = (displayType: string, item: JsonObject, index: number) => {
    setScreenshotDisplayType(displayType);
    setScreenshotLocale(textValue(item.locale) || 'zh-Hans');
    setScreenshotSlot(textValue(item.slot) || (displayType === UNASSIGNED_SCREENSHOT_DISPLAY_TYPE ? '' : displayType));
    setScreenshotOrder(textValue(item.order));
    setScreenshotReplaceIndex(String(index));
    setScreenshotFile(null);
    setSelectedExistingObjectName('');
  };

  const prepareExistingScreenshotSelection = (displayType: string) => {
    if (screenshotDisplayType === displayType && screenshotReplaceIndex.trim()) {
      setScreenshotFile(null);
      return;
    }
    prepareScreenshotAppend(displayType);
  };

  const materialOrderValue = () =>
    parseOptionalNonNegativeInt(screenshotOrder, t('appStoreReleaseWorkbench.errors.invalidScreenshotOrder'));

  const replaceIndexValue = () =>
    parseOptionalNonNegativeInt(screenshotReplaceIndex, t('appStoreReleaseWorkbench.errors.invalidReplaceIndex'));

  const moveScreenshotMaterial = (index: number, direction: -1 | 1) => {
    if (!draft) {
      return;
    }
    try {
      setEditorValue(moveScreenshotItemWithinGroup(editorValue, index, direction));
      clearPendingScreenshotReplacement();
      setEditorError(null);
    } catch {
      setEditorError(t('appStoreReleaseWorkbench.errors.invalidDraftJson'));
    }
  };

  const removeScreenshotMaterial = (index: number) => {
    if (!draft) {
      return;
    }
    try {
      setEditorValue(removeScreenshotItem(editorValue, index));
      clearPendingScreenshotReplacement();
      setEditorError(null);
    } catch {
      setEditorError(t('appStoreReleaseWorkbench.errors.invalidDraftJson'));
    }
  };

  const removeScreenshotGroup = (displayType: string) => {
    if (!draft) {
      return;
    }
    try {
      setEditorValue(removeScreenshotItemsByDisplayType(editorValue, displayType));
      clearPendingScreenshotReplacement();
      setEditorError(null);
    } catch {
      setEditorError(t('appStoreReleaseWorkbench.errors.invalidDraftJson'));
    }
  };

  const uploadScreenshot = () => {
    if (!draft || !screenshotFile || isDirty) {
      return;
    }
    let order: number | null;
    let replaceIndex: number | null;
    try {
      order = materialOrderValue();
      replaceIndex = replaceIndexValue();
    } catch (error) {
      setEditorError(error instanceof Error ? error.message : t('appStoreReleaseWorkbench.errors.invalidDraftJson'));
      return;
    }
    uploadScreenshotMutation.mutate(
      {
        base_snapshot_revision: draft.base_snapshot_revision,
        display_type: screenshotDisplayType,
        draft_revision_id: draft.current_revision_id,
        file: screenshotFile,
        locale: screenshotLocale,
        order,
        replace_index: replaceIndex,
        reason: reason || null,
        slot: screenshotSlot.trim() || null,
        spec_hash: draft.spec_hash,
      },
      {
        onSuccess: (nextDraft) => {
          setDraft(nextDraft);
          setEditorValue(toPrettyJson(nextDraft.draft_payload));
          setEditorError(null);
          setScreenshotFile(null);
          setScreenshotOrder('');
          setScreenshotReplaceIndex('');
          validateMutation.reset();
          resetReleaseFlow();
        },
      },
    );
  };

  const attachScreenshot = () => {
    if (!draft || !selectedExistingObjectName || isDirty) {
      return;
    }
    let order: number | null;
    let replaceIndex: number | null;
    try {
      order = materialOrderValue();
      replaceIndex = replaceIndexValue();
    } catch (error) {
      setEditorError(error instanceof Error ? error.message : t('appStoreReleaseWorkbench.errors.invalidDraftJson'));
      return;
    }
    attachScreenshotMutation.mutate(
      {
        base_snapshot_revision: draft.base_snapshot_revision,
        display_type: screenshotDisplayType,
        draft_revision_id: draft.current_revision_id,
        locale: screenshotLocale,
        object_name: selectedExistingObjectName,
        order,
        reason: reason || null,
        replace_index: replaceIndex,
        slot: screenshotSlot.trim() || null,
        spec_hash: draft.spec_hash,
      },
      {
        onSuccess: (nextDraft) => {
          setDraft(nextDraft);
          setEditorValue(toPrettyJson(nextDraft.draft_payload));
          setEditorError(null);
          setSelectedExistingObjectName('');
          setScreenshotOrder('');
          setScreenshotReplaceIndex('');
          validateMutation.reset();
          resetReleaseFlow();
        },
      },
    );
  };

  const validateDraft = () => {
    if (!draft) {
      return;
    }
    validateMutation.mutate({
      app_state: textValue(valueAt(latestSnapshot?.app_info ?? {}, 'state')) || null,
      base_payload: latestSnapshot ? snapshotToDraftPayload(latestSnapshot, versionString, selectedBuildKey) : null,
      draft_revision_id: draft.current_revision_id,
      promotional_text_change_reason: reason || null,
      shipped_features: DEFAULT_SUPPORTED_FEATURES,
      supported_features: DEFAULT_SUPPORTED_FEATURES,
      target_gate_evidence: null,
      version_state: versionState(selectedVersion) || null,
      visual_proof_features: DEFAULT_VISUAL_PROOF_FEATURES,
    });
  };

  const approveRelease = () => {
    if (!draft || !canApproveRelease) {
      return;
    }
    approveDraftMutation.mutate(
      {
        approved_hash: approvedHashValue,
        build_id: selectedBuildKey,
        draft_revision_id: draft.current_revision_id,
        release_mode: releaseMode,
        review_notes: reason || null,
        validation_run_id: null,
      },
      {
        onSuccess: (nextApproval) => {
          setApproval(nextApproval);
          setApprovedHash(nextApproval.approved_hash);
          setSubmission(null);
          setPublicStoreVerification(null);
        },
      },
    );
  };

  const submitRelease = () => {
    if (!approval || !canSubmitRelease) {
      return;
    }
    submitReleaseMutation.mutate(
      {
        approved_snapshot_hash: approval.approved_hash,
        build_id: selectedBuildKey,
        release_mode: releaseMode,
        review_notes: reason || null,
      },
      {
        onSuccess: (nextSubmission) => {
          setSubmission(nextSubmission);
          setPublicStoreVerification(null);
        },
      },
    );
  };

  const pollSubmission = () => {
    if (!submission) {
      return;
    }
    pollSubmissionMutation.mutate(undefined, {
      onSuccess: (nextSubmission) => setSubmission(nextSubmission),
    });
  };

  const verifyPublicStore = () => {
    if (!submission) {
      return;
    }
    verifyPublicStoreMutation.mutate(undefined, {
      onSuccess: (verification) => setPublicStoreVerification(verification),
    });
  };

  const refreshFromSnapshot = () => {
    if (!latestSnapshot || draft) {
      return;
    }
    setEditorValue(toPrettyJson(snapshotToDraftPayload(latestSnapshot, versionString, selectedBuildKey)));
    setEditorError(null);
  };

  const diffItems = diffQuery.data?.items ?? [];

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <AppWindowMac className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold text-foreground">{t('appStoreReleaseWorkbench.title')}</h1>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <StatusChip tone={draft ? 'green' : 'gray'}>
              {draft
                ? t('appStoreReleaseWorkbench.status.draftRevision', { revision: String(draft.revision_number) })
                : t('appStoreReleaseWorkbench.status.noDraft')}
            </StatusChip>
            <StatusChip tone={isDirty ? 'amber' : 'green'}>
              {isDirty
                ? t('appStoreReleaseWorkbench.status.unsavedEdits')
                : t('appStoreReleaseWorkbench.status.savedState')}
            </StatusChip>
            <StatusChip tone={validation?.has_blocking_errors ? 'red' : validation ? 'green' : 'gray'}>
              {validation
                ? (validation.has_blocking_errors
                    ? t('appStoreReleaseWorkbench.status.blocked')
                    : t('appStoreReleaseWorkbench.status.validationClear'))
                : t('appStoreReleaseWorkbench.status.notValidated')}
            </StatusChip>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label className="block w-64">
            <span className="mb-1 block text-sm font-medium text-foreground">
              {t('appStoreReleaseWorkbench.form.appSelect')}
            </span>
            <select
              className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              value={appKey}
              onChange={(event) => selectApp(event.target.value)}
            >
              <option value="">
                {appCatalogQuery.isLoading
                  ? t('appStoreReleaseWorkbench.form.loadingApps')
                  : t('appStoreReleaseWorkbench.form.selectApp')}
              </option>
              {appCatalog.map((app) => (
                <option key={app.id} value={app.app_key}>
                  {appCatalogLabel(app)}
                </option>
              ))}
            </select>
          </label>
          <div className="w-40">
            <Input
              label={t('appStoreReleaseWorkbench.form.version')}
              value={versionString}
              onChange={(event) => {
                setVersionString(event.target.value);
                if (!draft && latestSnapshot) {
                  setEditorValue(toPrettyJson(snapshotToDraftPayload(latestSnapshot, event.target.value, selectedBuildKey)));
                }
              }}
              placeholder="1.2.3"
            />
          </div>
          <label className="block w-56">
            <span className="mb-1 block text-sm font-medium text-foreground">
              {t('appStoreReleaseWorkbench.form.build')}
            </span>
            <select
              className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              value={selectedBuildKey}
              onChange={(event) => {
                setSelectedBuildKey(event.target.value);
                if (!draft && latestSnapshot) {
                  setEditorValue(toPrettyJson(snapshotToDraftPayload(latestSnapshot, versionString, event.target.value)));
                }
              }}
            >
              {buildOptions.length === 0 ? (
                <option value="">{t('appStoreReleaseWorkbench.form.noBuild')}</option>
              ) : (
                buildOptions.map((build, index) => (
                  <option key={`${buildKey(build)}-${index}`} value={buildKey(build)}>
                    {buildLabel(build)}
                  </option>
                ))
              )}
            </select>
          </label>
          <Button
            type="button"
            variant="outline"
            onClick={() => discoverAppsMutation.mutate()}
            disabled={discoverAppsMutation.isPending}
            loading={discoverAppsMutation.isPending}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('appStoreReleaseWorkbench.form.discover')}
          </Button>
          <Button
            type="button"
            onClick={() => syncMutation.mutate()}
            disabled={!appKey || syncMutation.isPending}
            loading={syncMutation.isPending}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('appStoreReleaseWorkbench.form.sync')}
          </Button>
          <Button
            type="button"
            onClick={createDraft}
            disabled={!latestSnapshot || Boolean(draft) || createDraftMutation.isPending}
            loading={createDraftMutation.isPending}
            title={!latestSnapshot ? t('appStoreReleaseWorkbench.form.createDraftDisabled') : undefined}
          >
            <GitPullRequest className="mr-2 h-4 w-4" />
            {t('appStoreReleaseWorkbench.form.createDraft')}
          </Button>
        </div>
      </div>

      <ErrorBanner message={reviewError ?? editorError} />

      {!appCatalogQuery.isLoading && appCatalog.length === 0 ? (
        <div className="rounded-lg border border-info/30 bg-info/10 px-4 py-4">
          <div className="font-medium text-info">
            {t('appStoreReleaseWorkbench.catalog.emptyTitle')}
          </div>
          <p className="mt-1 text-sm text-info">
            {t('appStoreReleaseWorkbench.catalog.emptyBody')}
          </p>
          <Button
            type="button"
            size="sm"
            className="mt-3"
            onClick={() => discoverAppsMutation.mutate()}
            disabled={discoverAppsMutation.isPending}
            loading={discoverAppsMutation.isPending}
          >
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            {t('appStoreReleaseWorkbench.form.discover')}
          </Button>
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-4">
        <Counter icon={BadgeCheck} label={t('appStoreReleaseWorkbench.counters.snapshot')} value={latestSnapshot?.snapshot_revision ?? '-'} />
        <Counter icon={FileJson} label={t('appStoreReleaseWorkbench.counters.spec')} value={latestSnapshot?.spec_version ?? '-'} />
        <Counter icon={Boxes} label={t('appStoreReleaseWorkbench.counters.versions')} value={latestSnapshot?.versions.length ?? 0} />
        <Counter icon={PackageCheck} label={t('appStoreReleaseWorkbench.counters.builds')} value={latestSnapshot?.builds.length ?? 0} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)] 2xl:grid-cols-[320px_minmax(0,1fr)_390px]">
        <Card className="min-w-0">
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">{t('appStoreReleaseWorkbench.snapshot.title')}</CardTitle>
            </div>
            <CardDescription className="text-xs">
              {selectedApp ? appCatalogLabel(selectedApp) : t('appStoreReleaseWorkbench.form.noAppSelected')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!appKey ? (
              <div className="rounded-lg border border-border bg-muted/40 px-3 py-4">
                <div className="font-medium text-foreground">
                  {t('appStoreReleaseWorkbench.form.noAppSelected')}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('appStoreReleaseWorkbench.catalog.selectAppBody')}
                </p>
              </div>
            ) : snapshotQuery.isLoading ? (
              <div className="text-sm text-muted-foreground">{t('appStoreReleaseWorkbench.snapshot.loading')}</div>
            ) : snapshotQuery.error && !snapshotNotFound ? (
              <ErrorBanner message={snapshotQuery.error.message} />
            ) : snapshotNotFound ? (
              <div className="rounded-lg border border-info/30 bg-info/10 px-3 py-4">
                <div className="font-medium text-info">
                  {t('appStoreReleaseWorkbench.snapshot.notSyncedTitle')}
                </div>
                <p className="mt-1 text-sm text-info">
                  {t('appStoreReleaseWorkbench.snapshot.notSyncedBody')}
                </p>
                <Button
                  type="button"
                  size="sm"
                  className="mt-3"
                  onClick={() => syncMutation.mutate()}
                  disabled={!appKey || syncMutation.isPending}
                  loading={syncMutation.isPending}
                >
                  <RefreshCw className="mr-2 h-3.5 w-3.5" />
                  {t('appStoreReleaseWorkbench.snapshot.syncFromAsc')}
                </Button>
              </div>
            ) : latestSnapshot ? (
              <>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-muted-foreground">{t('appStoreReleaseWorkbench.snapshot.bundle')}</span>
                    <span className="break-all text-right font-medium text-foreground">{latestSnapshot.bundle_id}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-muted-foreground">{t('appStoreReleaseWorkbench.snapshot.ascAppId')}</span>
                    <span className="break-all text-right font-medium text-foreground">
                      {formatValue(latestSnapshot.asc_app_id, t)}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-muted-foreground">{t('appStoreReleaseWorkbench.snapshot.specHash')}</span>
                    <span className="break-all text-right font-mono text-xs text-foreground">
                      {latestSnapshot.spec_hash}
                    </span>
                  </div>
                </div>
                <SnapshotItems snapshot={latestSnapshot} t={t} />
              </>
            ) : (
              <div className="text-sm text-muted-foreground">{t('appStoreReleaseWorkbench.snapshot.noSnapshot')}</div>
            )}
          </CardContent>
        </Card>

        <Card className="min-w-0">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <Code2 className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">{t('appStoreReleaseWorkbench.editor.title')}</CardTitle>
                </div>
                <CardDescription className="text-xs">
                  {draft ? draft.current_revision_id : t('appStoreReleaseWorkbench.editor.description')}
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={refreshFromSnapshot} disabled={!latestSnapshot || Boolean(draft)}>
                  <RefreshCw className="mr-2 h-3.5 w-3.5" />
                  {t('appStoreReleaseWorkbench.editor.reset')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={saveDraft}
                  disabled={!canSaveDraft}
                  loading={patchDraftMutation.isPending}
                  title={!draft ? t('appStoreReleaseWorkbench.editor.saveRequiresDraft') : undefined}
                >
                  <Save className="mr-2 h-3.5 w-3.5" />
                  {t('appStoreReleaseWorkbench.editor.saveDraft')}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className={cn(
                'rounded-lg border px-3 py-3 text-sm',
                workflowNotice.tone === 'blue' && 'border-info/30 bg-info/10 text-info',
                workflowNotice.tone === 'amber' && 'border-warning/30 bg-warning/10 text-warning',
                workflowNotice.tone === 'green' && 'border-success/30 bg-success/10 text-success',
              )}
            >
              <div className="font-medium">{workflowNotice.title}</div>
              <div className="mt-1">{workflowNotice.body}</div>
            </div>

            <div className="grid gap-3 lg:grid-cols-[1fr_220px]">
              <Input
                label={t('appStoreReleaseWorkbench.editor.changeReason')}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder={t('appStoreReleaseWorkbench.editor.defaultReason')}
              />
              <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                <div className="font-medium text-foreground">{t('appStoreReleaseWorkbench.editor.staleGuards')}</div>
                <div className="mt-1 break-all">
                  {t('appStoreReleaseWorkbench.editor.baseGuard')} {draft?.base_snapshot_revision ?? latestSnapshot?.snapshot_revision ?? '-'}
                </div>
                <div className="break-all">
                  {t('appStoreReleaseWorkbench.editor.specGuard')} {draft?.spec_hash ?? latestSnapshot?.spec_hash ?? '-'}
                </div>
              </div>
            </div>

            <Tabs value={editorTab} onValueChange={setEditorTab} className="space-y-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="aso" className="flex items-center gap-2">
                  <FileJson className="h-4 w-4" />
                  {t('appStoreReleaseWorkbench.editor.asoFieldsTitle')}
                </TabsTrigger>
                <TabsTrigger value="media" className="flex items-center gap-2">
                  <Images className="h-4 w-4" />
                  {t('appStoreReleaseWorkbench.editor.screenshotMaterials')}
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {screenshotMaterials.length}
                  </span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="aso" className="space-y-4">
                <div className="space-y-3 rounded-lg border border-border px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <FileJson className="h-4 w-4 text-muted-foreground" />
                      <div className="text-sm font-semibold text-foreground">
                        {t('appStoreReleaseWorkbench.editor.asoFieldsTitle')}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t('appStoreReleaseWorkbench.editor.asoFieldsDescription')}
                    </div>
                  </div>
                  <div className="grid gap-3 lg:grid-cols-2">
                    <Input
                      aria-label={t('appStoreReleaseWorkbench.editor.titleField')}
                      label={t('appStoreReleaseWorkbench.editor.titleField')}
                      value={textValue(metadataPayload.title)}
                      onChange={(event) => updateMetadataField('title', event.target.value)}
                      disabled={!isDraftEditable}
                      helperText={fieldLimitLabel(textValue(metadataPayload.title), 30, t)}
                    />
                    <Input
                      aria-label={t('appStoreReleaseWorkbench.editor.subtitle')}
                      label={t('appStoreReleaseWorkbench.editor.subtitle')}
                      value={textValue(metadataPayload.subtitle)}
                      onChange={(event) => updateMetadataField('subtitle', event.target.value)}
                      disabled={!isDraftEditable}
                      helperText={fieldLimitLabel(textValue(metadataPayload.subtitle), 30, t)}
                    />
                    <Input
                      aria-label={t('appStoreReleaseWorkbench.editor.keywords')}
                      label={t('appStoreReleaseWorkbench.editor.keywords')}
                      value={textValue(metadataPayload.keywords)}
                      onChange={(event) => updateMetadataField('keywords', event.target.value)}
                      disabled={!isDraftEditable}
                      helperText={fieldLimitLabel(textValue(metadataPayload.keywords), 100, t)}
                    />
                    <Input
                      aria-label={t('appStoreReleaseWorkbench.editor.privacyPolicyUrl')}
                      label={t('appStoreReleaseWorkbench.editor.privacyPolicyUrl')}
                      value={textValue(metadataPayload.privacy_policy_url)}
                      onChange={(event) => updateMetadataField('privacy_policy_url', event.target.value)}
                      disabled={!isDraftEditable}
                      placeholder="https://doratech.cn/privacy.html"
                    />
                  </div>
                  <div className="grid gap-3 xl:grid-cols-2">
                    <Textarea
                      aria-label={t('appStoreReleaseWorkbench.editor.descriptionField')}
                      label={t('appStoreReleaseWorkbench.editor.descriptionField')}
                      className="min-h-[140px]"
                      value={textValue(metadataPayload.description)}
                      onChange={(event) => updateMetadataField('description', event.target.value)}
                      disabled={!isDraftEditable}
                      helperText={fieldLimitLabel(textValue(metadataPayload.description), 4000, t)}
                    />
                    <Textarea
                      aria-label={t('appStoreReleaseWorkbench.editor.whatsNew')}
                      label={t('appStoreReleaseWorkbench.editor.whatsNew')}
                      className="min-h-[140px]"
                      value={textValue(metadataPayload.whats_new)}
                      onChange={(event) => updateMetadataField('whats_new', event.target.value)}
                      disabled={!isDraftEditable}
                      helperText={fieldLimitLabel(textValue(metadataPayload.whats_new), 4000, t)}
                    />
                    <Textarea
                      aria-label={t('appStoreReleaseWorkbench.editor.promotionalText')}
                      label={t('appStoreReleaseWorkbench.editor.promotionalText')}
                      className="min-h-[96px]"
                      value={textValue(metadataPayload.promotional_text)}
                      onChange={(event) => updateMetadataField('promotional_text', event.target.value)}
                      disabled={!isDraftEditable}
                      helperText={fieldLimitLabel(textValue(metadataPayload.promotional_text), 170, t)}
                    />
                    <Textarea
                      aria-label={t('appStoreReleaseWorkbench.editor.subscriptionDisclosure')}
                      label={t('appStoreReleaseWorkbench.editor.subscriptionDisclosure')}
                      className="min-h-[96px]"
                      value={textValue(metadataPayload.subscription_disclosure)}
                      onChange={(event) => updateMetadataField('subscription_disclosure', event.target.value)}
                      disabled={!isDraftEditable}
                      helperText={fieldLimitLabel(textValue(metadataPayload.subscription_disclosure), 1200, t)}
                    />
                    <Textarea
                      aria-label={t('appStoreReleaseWorkbench.editor.disclaimer')}
                      label={t('appStoreReleaseWorkbench.editor.disclaimer')}
                      className="min-h-[80px] xl:col-span-2"
                      value={textValue(metadataPayload.disclaimer)}
                      onChange={(event) => updateMetadataField('disclaimer', event.target.value)}
                      disabled={!isDraftEditable}
                      helperText={fieldLimitLabel(textValue(metadataPayload.disclaimer), 1200, t)}
                    />
                  </div>
                </div>

                <div className="grid gap-3 lg:grid-cols-[1fr_auto]">
                  <Input
                    label={t('appStoreReleaseWorkbench.editor.asoSourcePath')}
                    value={asoSourcePath}
                    onChange={(event) => setAsoSourcePath(event.target.value)}
                    placeholder="docs/aso/v4-dora-astrologer.md"
                    disabled={!isDraftEditable}
                  />
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={importAso}
                      disabled={!draft || !asoSourcePath.trim() || importAsoMutation.isPending}
                      loading={importAsoMutation.isPending}
                    >
                      <FileInput className="mr-2 h-4 w-4" />
                      {t('appStoreReleaseWorkbench.editor.importAso')}
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="media" className="space-y-4">
                <section className="rounded-lg border border-border bg-card px-4 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex min-w-0 gap-3">
                      <Images className="mt-1 h-5 w-5 flex-none text-muted-foreground" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground">
                          {t('appStoreReleaseWorkbench.editor.ascCurrentMaterials')}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {t('appStoreReleaseWorkbench.editor.ascCurrentMaterialsDescription')}
                        </div>
                      </div>
                    </div>
                    <StatusChip tone={ascScreenshotMaterials.length > 0 ? 'blue' : 'gray'}>
                      {t('appStoreReleaseWorkbench.common.items', { count: String(ascScreenshotMaterials.length) })}
                    </StatusChip>
                  </div>

                  {ascScreenshotMaterials.length > 0 ? (
                    <div className="mt-4 space-y-4">
                      {groupedAscScreenshotMaterials.map((group) => (
                        <div key={`asc-${group.displayType}`} className="space-y-2">
                          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span className="font-medium text-foreground">
                              {screenshotDisplayTypeLabel(group.displayType, t)}
                            </span>
                            <span>
                              {t('appStoreReleaseWorkbench.editor.screenshotCount', {
                                count: String(group.items.length),
                              })}
                            </span>
                          </div>
                          <div className="flex min-h-[150px] gap-3 overflow-x-auto pb-2">
                            {group.items.map(({ item, index }, groupIndex) => {
                              const previewUrl = screenshotPreviewUrl(item, fileByObjectName);
                              const itemName =
                                textValue(item.file_name) ||
                                textValue(item.name) ||
                                textValue(item.asc_resource_id) ||
                                textValue(item.object_name);
                              const dimensions = screenshotItemDimensionsLabel(item);
                              return (
                                <div key={`asc-${textValue(item.asc_resource_id) || index}`} className="w-[88px] flex-none">
                                  <div className="relative aspect-[9/19.5] overflow-hidden rounded-md border border-border bg-muted">
                                    {previewUrl ? (
                                      <img src={previewUrl} alt={itemName} className="h-full w-full object-cover" />
                                    ) : (
                                      <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                                        <ImageIcon className="h-5 w-5" />
                                      </div>
                                    )}
                                    <span className="absolute left-1 top-1 rounded bg-background/90 px-1.5 py-0.5 text-[10px] font-medium text-foreground shadow-sm">
                                      {groupIndex + 1}
                                    </span>
                                  </div>
                                  <div className="mt-2 truncate text-xs font-medium text-foreground" title={itemName}>
                                    {formatValue(itemName, t)}
                                  </div>
                                  <div className="mt-1 truncate text-[11px] text-muted-foreground">
                                    {[dimensions, textValue(item.locale)].filter(Boolean).join(' · ') || '-'}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-4 rounded-md border border-dashed border-border bg-muted/30 px-3 py-6 text-sm text-muted-foreground">
                      {t('appStoreReleaseWorkbench.editor.ascCurrentEmpty')}
                    </div>
                  )}
                </section>

                <div className="space-y-3">
                  {groupedScreenshotMaterials.map((group) => {
                    const isActiveGroup = group.displayType === screenshotDisplayType;
                    const dimensions = screenshotDimensionsLabel(latestSpec?.spec, group.displayType, t);
                    const pendingReplaceIndex = screenshotReplaceIndex.trim() ? Number(screenshotReplaceIndex) : null;
                    const pendingReplacePosition =
                      pendingReplaceIndex === null
                        ? 0
                        : group.items.findIndex((entry) => entry.index === pendingReplaceIndex) + 1;
                    const pendingTargetLabel =
                      pendingReplacePosition > 0
                        ? t('appStoreReleaseWorkbench.editor.replaceMaterial', {
                            index: String(pendingReplacePosition),
                          })
                        : t('appStoreReleaseWorkbench.editor.appendMaterial');
                    const canUploadGroup = canUploadScreenshot && isActiveGroup;
                    const canAttachGroup = canAttachScreenshot && isActiveGroup;

                    return (
                      <section
                        key={group.displayType}
                        className={cn(
                          'rounded-lg border bg-card px-4 py-3',
                          isActiveGroup ? 'border-primary/40 shadow-sm' : 'border-border',
                        )}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="flex min-w-0 gap-3">
                            <AppWindowMac className="mt-1 h-5 w-5 flex-none text-muted-foreground" />
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-foreground">
                                {screenshotDisplayTypeLabel(group.displayType, t)}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                <span>
                                  {t('appStoreReleaseWorkbench.editor.screenshotCount', {
                                    count: String(group.items.length),
                                  })}
                                </span>
                                <span>{dimensions}</span>
                              </div>
                            </div>
                          </div>
                          {isActiveGroup ? (
                            <StatusChip tone="blue">{t('appStoreReleaseWorkbench.editor.activeDevice')}</StatusChip>
                          ) : null}
                        </div>

                        <div className="mt-4 flex min-h-[190px] gap-3 overflow-x-auto pb-2">
                          {group.items.length > 0 ? (
                            group.items.map(({ item, index }, groupIndex) => {
                              const previewUrl = screenshotPreviewUrl(item, fileByObjectName);
                              const itemName = textValue(item.file_name) || textValue(item.name) || textValue(item.object_name);
                              const itemDimensions = screenshotItemDimensionsLabel(item);
                              const replaceFileLabel = t('appStoreReleaseWorkbench.editor.replaceScreenshotFile', {
                                displayType: screenshotDisplayTypeLabel(group.displayType, t),
                                index: String(groupIndex + 1),
                              });

                              return (
                                <div key={`${formatValue(item.object_name, t)}-${index}`} className="w-[112px] flex-none">
                                  <div className="relative aspect-[9/19.5] overflow-hidden rounded-md border border-border bg-muted">
                                    {previewUrl ? (
                                      <img src={previewUrl} alt={itemName} className="h-full w-full object-cover" />
                                    ) : (
                                      <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                                        <ImageIcon className="h-5 w-5" />
                                      </div>
                                    )}
                                    <span className="absolute left-1 top-1 rounded bg-background/90 px-1.5 py-0.5 text-[10px] font-medium text-foreground shadow-sm">
                                      {groupIndex + 1}
                                    </span>
                                    <div className="absolute right-1 top-1 flex gap-1">
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="h-6 w-6 bg-background/90 px-0"
                                        onClick={() => moveScreenshotMaterial(index, -1)}
                                        disabled={!isDraftEditable || groupIndex === 0}
                                        title={t('appStoreReleaseWorkbench.editor.moveUp')}
                                      >
                                        <ArrowUp className="h-3.5 w-3.5" />
                                        <span className="sr-only">{t('appStoreReleaseWorkbench.editor.moveUp')}</span>
                                      </Button>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="h-6 w-6 bg-background/90 px-0"
                                        onClick={() => moveScreenshotMaterial(index, 1)}
                                        disabled={!isDraftEditable || groupIndex === group.items.length - 1}
                                        title={t('appStoreReleaseWorkbench.editor.moveDown')}
                                      >
                                        <ArrowDown className="h-3.5 w-3.5" />
                                        <span className="sr-only">{t('appStoreReleaseWorkbench.editor.moveDown')}</span>
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="mt-2 truncate text-xs font-medium text-foreground" title={itemName}>
                                    {formatValue(itemName, t)}
                                  </div>
                                  <div className="mt-1 truncate text-[11px] text-muted-foreground">
                                    {[itemDimensions, textValue(item.locale)].filter(Boolean).join(' · ') || '-'}
                                  </div>
                                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                                    <label
                                      className={cn(
                                        'inline-flex cursor-pointer items-center text-primary hover:underline',
                                        !isDraftEditable && 'pointer-events-none cursor-not-allowed opacity-60',
                                      )}
                                      onClick={() => {
                                        prepareScreenshotReplacement(group.displayType, item, index);
                                      }}
                                    >
                                      {t('appStoreReleaseWorkbench.editor.replaceWithFile')}
                                      <input
                                        aria-label={replaceFileLabel}
                                        type="file"
                                        accept="image/png,image/jpeg"
                                        className="sr-only"
                                        disabled={!isDraftEditable}
                                        onChange={(event) => setScreenshotFile(event.target.files?.[0] ?? null)}
                                      />
                                    </label>
                                    <button
                                      type="button"
                                      className="text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                                      onClick={() => prepareScreenshotReplacement(group.displayType, item, index)}
                                      disabled={!isDraftEditable}
                                    >
                                      {t('appStoreReleaseWorkbench.editor.replaceWithExisting')}
                                    </button>
                                    <button
                                      type="button"
                                      className="text-destructive hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                                      onClick={() => removeScreenshotMaterial(index)}
                                      disabled={!isDraftEditable}
                                    >
                                      {t('appStoreReleaseWorkbench.editor.removeScreenshot')}
                                    </button>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="flex w-full flex-col items-center justify-center gap-3 rounded-md border border-dashed border-border bg-muted/30 px-3 py-8 text-sm text-muted-foreground">
                              <div>{t('appStoreReleaseWorkbench.editor.screenshotGroupEmpty')}</div>
                              <label
                                className={cn(
                                  'inline-flex cursor-pointer items-center gap-1 text-primary hover:underline',
                                  !isDraftEditable && 'pointer-events-none cursor-not-allowed opacity-60',
                                )}
                                onClick={() => {
                                  prepareScreenshotAppend(group.displayType);
                                  setSelectedExistingObjectName('');
                                }}
                              >
                                <Upload className="h-3.5 w-3.5" />
                                {t('appStoreReleaseWorkbench.editor.chooseFile')}
                                <input
                                  aria-label={t('appStoreReleaseWorkbench.editor.screenshotFileForDisplayType', {
                                    displayType: screenshotDisplayTypeLabel(group.displayType, t),
                                  })}
                                  type="file"
                                  accept="image/png,image/jpeg"
                                  className="sr-only"
                                  disabled={!isDraftEditable}
                                  onChange={(event) => setScreenshotFile(event.target.files?.[0] ?? null)}
                                />
                              </label>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3 text-sm">
                          <span className="text-muted-foreground">
                            {t('appStoreReleaseWorkbench.editor.screenshotCount', {
                              count: String(group.items.length),
                            })}
                          </span>
                          <label
                            className={cn(
                              'inline-flex cursor-pointer items-center gap-1 text-primary hover:underline',
                              !isDraftEditable && 'pointer-events-none cursor-not-allowed opacity-60',
                            )}
                            onClick={() => {
                              prepareScreenshotAppend(group.displayType);
                              setSelectedExistingObjectName('');
                            }}
                          >
                            <Upload className="h-3.5 w-3.5" />
                            {t('appStoreReleaseWorkbench.editor.chooseFile')}
                            <input
                              aria-label={t('appStoreReleaseWorkbench.editor.screenshotFileForDisplayType', {
                                displayType: screenshotDisplayTypeLabel(group.displayType, t),
                              })}
                              type="file"
                              accept="image/png,image/jpeg"
                              className="sr-only"
                              disabled={!isDraftEditable}
                              onChange={(event) => setScreenshotFile(event.target.files?.[0] ?? null)}
                            />
                          </label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="px-2 text-primary hover:bg-primary/10"
                            onClick={uploadScreenshot}
                            disabled={!canUploadGroup}
                            loading={uploadScreenshotMutation.isPending && isActiveGroup}
                            title={!draft ? t('appStoreReleaseWorkbench.editor.screenshotUploadRequiresDraft') : undefined}
                          >
                            {t('appStoreReleaseWorkbench.editor.uploadScreenshot')}
                          </Button>
                          <select
                            aria-label={t('appStoreReleaseWorkbench.editor.existingScreenshotForDisplayType', {
                              displayType: screenshotDisplayTypeLabel(group.displayType, t),
                            })}
                            className="min-w-[220px] rounded-lg border border-input bg-card px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
                            value={isActiveGroup ? selectedExistingObjectName : ''}
                            onFocus={() => prepareExistingScreenshotSelection(group.displayType)}
                            onChange={(event) => {
                              prepareExistingScreenshotSelection(group.displayType);
                              setSelectedExistingObjectName(event.target.value);
                            }}
                            disabled={!isDraftEditable || userImageFilesQuery.isLoading}
                          >
                            <option value="">
                              {userImageFilesQuery.isLoading
                                ? t('appStoreReleaseWorkbench.editor.loadingImageFiles')
                                : t('appStoreReleaseWorkbench.editor.selectExistingScreenshot')}
                            </option>
                            {userImageFiles.map((file) => (
                              <option key={file.object_name} value={file.object_name}>
                                {imageFileLabel(file, t)}
                              </option>
                            ))}
                          </select>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="px-2"
                            onClick={attachScreenshot}
                            disabled={!canAttachGroup}
                            loading={attachScreenshotMutation.isPending && isActiveGroup}
                            title={!draft ? t('appStoreReleaseWorkbench.editor.screenshotUploadRequiresDraft') : undefined}
                          >
                            <FolderOpen className="mr-1 h-3.5 w-3.5" />
                            {t('appStoreReleaseWorkbench.editor.attachExistingScreenshot')}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => removeScreenshotGroup(group.displayType)}
                            disabled={!isDraftEditable || group.items.length === 0}
                          >
                            <Trash2 className="mr-1 h-3.5 w-3.5" />
                            {t('appStoreReleaseWorkbench.editor.deleteAllScreenshots')}
                          </Button>
                          {isActiveGroup && (screenshotFile || selectedExistingFile || pendingReplacePosition > 0) ? (
                            <span className="rounded-md border border-border bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
                              {pendingTargetLabel}
                              {screenshotFile
                                ? ` · ${t('appStoreReleaseWorkbench.editor.selectedFile', { filename: screenshotFile.name })}`
                                : null}
                              {!screenshotFile && selectedExistingFile ? ` · ${selectedExistingFile.filename}` : null}
                            </span>
                          ) : null}
                        </div>
                      </section>
                    );
                  })}
                </div>
              </TabsContent>
            </Tabs>

            <Textarea
              aria-label={t('appStoreReleaseWorkbench.editor.payloadJson')}
              label={t('appStoreReleaseWorkbench.editor.payloadJson')}
              className="min-h-[280px] font-mono text-xs leading-5"
              value={editorValue}
              onChange={(event) => {
                setEditorValue(event.target.value);
                setEditorError(null);
              }}
              disabled={!isDraftEditable}
              error={editorError ?? undefined}
            />

          </CardContent>
        </Card>

        <div className="space-y-4 min-w-0 xl:col-span-2 2xl:col-span-1">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Diff className="h-4 w-4 text-muted-foreground" />
                    <CardTitle className="text-base">{t('appStoreReleaseWorkbench.review.title')}</CardTitle>
                  </div>
                  <CardDescription className="text-xs">
                    {t('appStoreReleaseWorkbench.review.description')}
                  </CardDescription>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={validateDraft}
                  disabled={!canValidateDraft}
                  loading={validateMutation.isPending}
                  title={!draft || isDirty ? t('appStoreReleaseWorkbench.review.validateRequiresSavedDraft') : undefined}
                >
                  <ShieldCheck className="mr-2 h-3.5 w-3.5" />
                  {t('appStoreReleaseWorkbench.review.validate')}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-2 text-center">
                  <div className="text-lg font-semibold text-destructive">{counts.blocking}</div>
                  <div className="text-xs text-destructive">{t('appStoreReleaseWorkbench.severity.blocking')}</div>
                </div>
                <div className="rounded-lg border border-warning/30 bg-warning/10 p-2 text-center">
                  <div className="text-lg font-semibold text-warning">{counts.warning}</div>
                  <div className="text-xs text-warning">{t('appStoreReleaseWorkbench.severity.warning')}</div>
                </div>
                <div className="rounded-lg border border-info/30 bg-info/10 p-2 text-center">
                  <div className="text-lg font-semibold text-info">{counts.info}</div>
                  <div className="text-xs text-info">{t('appStoreReleaseWorkbench.severity.info')}</div>
                </div>
              </div>

              {validation && validation.items.length === 0 && (
                <div className="flex items-center gap-2 rounded-lg border border-success/30 bg-success/10 px-3 py-3 text-sm text-success">
                  <CheckCircle2 className="h-4 w-4" />
                  {t('appStoreReleaseWorkbench.review.noFindings')}
                </div>
              )}

              {validation?.items.map((item) => (
                <ValidationItem key={`${item.rule_id}-${item.field_path}`} item={item} t={t} />
              ))}

              {!validation && (
                <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-3 text-sm text-muted-foreground">
                  <AlertTriangle className="h-4 w-4" />
                  {t('appStoreReleaseWorkbench.review.awaitingValidation')}
                </div>
              )}

              <div className="border-t border-border pt-4">
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm font-semibold text-foreground">{t('appStoreReleaseWorkbench.diff.title')}</div>
                  <StatusChip tone={diffItems.length > 0 ? 'amber' : 'gray'}>
                    {diffQuery.isFetching
                      ? t('appStoreReleaseWorkbench.diff.loading')
                      : t('appStoreReleaseWorkbench.common.items', { count: String(diffItems.length) })}
                  </StatusChip>
                </div>
                <div className="max-h-[520px] space-y-3 overflow-auto pr-1">
                  {diffItems.map((item) => (
                    <DiffItem key={`${item.category}-${item.path}-${item.change_type}`} item={item} t={t} />
                  ))}
                  {draft && !diffQuery.isFetching && diffItems.length === 0 && (
                    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-3 text-sm text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4" />
                      {t('appStoreReleaseWorkbench.diff.noSavedDraftDiff')}
                    </div>
                  )}
                  {!draft && (
                    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-3 text-sm text-muted-foreground">
                      <XCircle className="h-4 w-4" />
                      {t('appStoreReleaseWorkbench.diff.noDraftSelected')}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {releaseControlsAvailable && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Rocket className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">{t('appStoreReleaseWorkbench.release.title')}</CardTitle>
                </div>
                <CardDescription className="text-xs">
                  {t('appStoreReleaseWorkbench.release.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 lg:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-foreground">
                      {t('appStoreReleaseWorkbench.release.build')}
                    </span>
                    <select
                      className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      value={selectedBuildKey}
                      onChange={(event) => setSelectedBuildKey(event.target.value)}
                    >
                      {buildOptions.length === 0 ? (
                        <option value="">{t('appStoreReleaseWorkbench.form.noBuild')}</option>
                      ) : (
                        buildOptions.map((build, index) => (
                          <option key={`${buildKey(build)}-${index}`} value={buildKey(build)}>
                            {buildLabel(build)}
                          </option>
                        ))
                      )}
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-foreground">
                      {t('appStoreReleaseWorkbench.release.mode')}
                    </span>
                    <select
                      className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      value={releaseMode}
                      onChange={(event) => setReleaseMode(event.target.value as AppStoreReleaseMode)}
                    >
                      <option value="manual">{t('appStoreReleaseWorkbench.release.modeManual')}</option>
                      <option value="automatic">{t('appStoreReleaseWorkbench.release.modeAutomatic')}</option>
                    </select>
                  </label>
                </div>

                <div className="grid gap-3 lg:grid-cols-[1fr_auto]">
                  <Input
                    aria-label={t('appStoreReleaseWorkbench.release.approvedHash')}
                    label={t('appStoreReleaseWorkbench.release.approvedHash')}
                    value={approvedHash}
                    readOnly
                    placeholder={approvalInfoQuery.isFetching ? t('appStoreReleaseWorkbench.release.calculatingHash') : ''}
                  />
                  <div className="flex items-end">
                    <Button
                      type="button"
                      onClick={approveRelease}
                      disabled={!canApproveRelease}
                      loading={approveDraftMutation.isPending}
                    >
                      <ShieldCheck className="mr-2 h-4 w-4" />
                      {t('appStoreReleaseWorkbench.release.approveSnapshot')}
                    </Button>
                  </div>
                </div>

                <div className="rounded-lg border border-border bg-muted/40 px-3 py-3 text-xs text-foreground">
                  <div className="font-medium text-foreground">{t('appStoreReleaseWorkbench.release.expectedHash')}</div>
                  <div className="mt-1 break-all font-mono">
                    {approvalInfoQuery.isFetching
                      ? t('appStoreReleaseWorkbench.release.calculatingHash')
                      : expectedApprovalHash || '-'}
                  </div>
                  {!approvedHashMatchesDraft && approvedHashValue && expectedApprovalHash && (
                    <div className="mt-2 text-destructive">{t('appStoreReleaseWorkbench.release.hashMismatch')}</div>
                  )}
                </div>

                {approval ? (
                  <div className="space-y-2 rounded-lg border border-success/30 bg-success/10 px-3 py-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium text-success">
                        {t('appStoreReleaseWorkbench.release.approvalState')}
                      </span>
                      <StatusChip tone="green">{releaseStateLabel(approval.state, t)}</StatusChip>
                    </div>
                    <div className="grid gap-2 text-xs text-success md:grid-cols-2">
                      <div className="break-all">
                        {t('appStoreReleaseWorkbench.release.snapshot')} {approval.snapshot_id}
                      </div>
                      <div>
                        {t('appStoreReleaseWorkbench.release.mode')} {releaseModeLabel(approval.release_mode, t)}
                      </div>
                      <div>
                        {t('appStoreReleaseWorkbench.release.build')} {approval.build_id}
                      </div>
                      <div className="break-all">
                        {t('appStoreReleaseWorkbench.release.approvedHash')} {approval.approved_hash}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-border bg-muted/40 px-3 py-3 text-sm text-muted-foreground">
                    {t('appStoreReleaseWorkbench.release.noApproval')}
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    onClick={submitRelease}
                    disabled={!canSubmitRelease}
                    loading={submitReleaseMutation.isPending}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    {t('appStoreReleaseWorkbench.release.submitReview')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={pollSubmission}
                    disabled={!submission || pollSubmissionMutation.isPending}
                    loading={pollSubmissionMutation.isPending}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    {t('appStoreReleaseWorkbench.release.pollStatus')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={verifyPublicStore}
                    disabled={!submission || verifyPublicStoreMutation.isPending}
                    loading={verifyPublicStoreMutation.isPending}
                  >
                    <SearchCheck className="mr-2 h-4 w-4" />
                    {t('appStoreReleaseWorkbench.release.verifyPublicStore')}
                  </Button>
                </div>

                {approval && !approvalMatchesSelection && (
                  <div className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-3 text-sm text-warning">
                    {t('appStoreReleaseWorkbench.release.submitGuard')}
                  </div>
                )}

                {submission ? (
                  <div className="space-y-3 rounded-lg border border-border px-3 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-foreground">
                        {t('appStoreReleaseWorkbench.release.submissionState')}
                      </span>
                      <StatusChip tone={submission.last_error ? 'red' : 'blue'}>
                        {releaseStateLabel(submission.state, t)}
                      </StatusChip>
                    </div>
                    {submission.last_error && (
                      <div className="rounded bg-destructive/10 px-2 py-2 text-xs text-destructive">{submission.last_error}</div>
                    )}
                    <div className="space-y-2">
                      <div className="text-xs font-semibold uppercase text-muted-foreground">
                        {t('appStoreReleaseWorkbench.release.timeline')}
                      </div>
                      {submission.events.length === 0 ? (
                        <div className="text-sm text-muted-foreground">
                          {t('appStoreReleaseWorkbench.release.noSubmissionEvents')}
                        </div>
                      ) : (
                        submission.events.map((event, index) => (
                          <div
                            key={`${event.state ?? 'event'}-${event.created_at ?? index}`}
                            className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <span className="font-medium text-foreground">
                                {releaseStateLabel(event.state, t)}
                              </span>
                              <span className="text-muted-foreground">{formatValue(event.created_at, t)}</span>
                            </div>
                            <div className="mt-1 text-foreground">{eventMessage(event, t)}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-border bg-muted/40 px-3 py-3 text-sm text-muted-foreground">
                    {t('appStoreReleaseWorkbench.release.noSubmission')}
                  </div>
                )}

                <div className="rounded-lg border border-border px-3 py-3">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="text-sm font-medium text-foreground">
                      {t('appStoreReleaseWorkbench.release.publicStore')}
                    </div>
                    {publicStoreVerification ? (
                      <StatusChip tone={publicStoreVerification.is_released ? 'green' : 'amber'}>
                        {publicStoreVerification.is_released
                          ? t('appStoreReleaseWorkbench.release.verifyReleased')
                          : t('appStoreReleaseWorkbench.release.verifyNotReleased')}
                      </StatusChip>
                    ) : null}
                  </div>
                  {publicStoreVerification ? (
                    <div className="grid gap-2 text-xs md:grid-cols-2">
                      <div>
                        <div className="text-muted-foreground">{t('appStoreReleaseWorkbench.release.bundle')}</div>
                        <div className="break-all font-medium text-foreground">{publicStoreVerification.bundle_id}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">{t('appStoreReleaseWorkbench.release.storefront')}</div>
                        <div className="font-medium text-foreground">{publicStoreVerification.storefront}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">{t('appStoreReleaseWorkbench.release.expectedVersion')}</div>
                        <div className="font-medium text-foreground">{publicStoreVerification.expected_version}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">{t('appStoreReleaseWorkbench.release.publicVersion')}</div>
                        <div className="font-medium text-foreground">
                          {formatValue(publicStoreVerification.public_version, t)}
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <div className="text-muted-foreground">{t('appStoreReleaseWorkbench.release.trackViewUrl')}</div>
                        <div className="break-all font-medium text-foreground">
                          {formatValue(publicStoreVerification.track_view_url, t)}
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <div className="text-muted-foreground">{t('appStoreReleaseWorkbench.release.checkedAt')}</div>
                        <div className="font-medium text-foreground">
                          {formatValue(publicStoreVerification.checked_at, t)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      {t('appStoreReleaseWorkbench.release.noVerification')}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Images className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base">{t('appStoreReleaseWorkbench.spec.title')}</CardTitle>
              </div>
              <CardDescription className="text-xs">{latestSpec?.spec_hash ?? latestSnapshot?.spec_hash ?? '-'}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {specRequirementRows(latestSpec?.spec, t).map(([field, requirement, source]) => (
                  <div
                    key={field}
                    className="grid gap-2 rounded-lg border border-border px-3 py-2 text-xs sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_auto]"
                  >
                    <div className="min-w-0 break-words font-mono text-foreground">{field}</div>
                    <div className="min-w-0 text-foreground">{requirement}</div>
                    <div className="justify-self-start sm:justify-self-end">
                      <StatusChip tone={source === 'schema' ? 'blue' : 'gray'}>
                        {source === 'schema'
                          ? t('appStoreReleaseWorkbench.spec.schemaSource')
                          : t('appStoreReleaseWorkbench.spec.defaultSource')}
                      </StatusChip>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
